import { LitElement, PropertyValues, html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@spectrum-web-components/bundle/elements.js'
import { styles } from './styles'

import '@shoelace-style/shoelace/dist/components/card/card.js'
import '@shoelace-style/shoelace/dist/components/button/button.js'

import { styles as sharedStyles } from '../../styles/shared-styles'

import './components/day-of-week/day-of-week'
import './components/time-range/time-range'
import './components/temp-toggle-open/temp-toggle-open'
import './components/regular-hours/regular-hours'
import './components/holidays-hours'
import { OpeningHours } from './types/opening-hours'
import { mapCustomToSchemaOrg, mapSchemaOrgToCustom } from './common/mapper-regular-hours'
import { API_ENDPOINTS } from '../../config/api.config'
import { ApiService } from '../../utils/api'
import { formatTime } from '../../utils/time'
import { OpeningTimeEntry } from '../../utils/types/api-types'
import { createBasicAuthHeader } from '../../utils/api'
import { DAY_ORDER_FULL, DAY_ORDER_SHORT } from '../../utils/constants'

type OpeningTimePayload = {
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
  sun: boolean
  open: number
  close: number
  restaurantId?: number
  hhmmOpen?: string
  hhmmClose?: string
}

@customElement('opening-hours')
export class OpeningHoursCmp extends LitElement {

  @property({type: Array}) openingHours: OpeningHours[] = []
  @property({type: Array}) initialOpeningHours: any = []
  @property({type: Array}) restaurantId: any = undefined
  @property({type: String}) key: string = ''
  @state() private isSavingRegularHours = false
  @state() private openingHoursLoaded = false
  @state() private regularHoursError = ''
  private pendingRegularHours: OpeningHours[] | null = null

  protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
   void this.getInitialOpeningHours()
  }

  protected async getInitialOpeningHours(): Promise<void> {
    const numericId = parseInt(this.restaurantId, 10)
    this.openingHoursLoaded = false

    try {
      const data = await ApiService.get<OpeningTimeEntry[]>(
        API_ENDPOINTS.openingTimes(numericId),
        { restaurantId: this.restaurantId, key: this.key }
      )
    
      this.initialOpeningHours = data
      this.openingHours = mapCustomToSchemaOrg(data)
      this.openingHoursLoaded = true

      console.log('Fetched and transformed opening hours:', this.openingHours)
    } catch (error) {
      console.error('Error fetching opening hours:', error)
      this.regularHoursError = 'Öffnungszeiten konnten nicht geladen werden.'
    }
  }



  handleTemporaryHours(evt: CustomEvent) {
    console.log(evt.detail)
    // todo: add temporary hours to opening hours and PUT on server 
  }

  handleRegularHours(evt: CustomEvent) {
    const updatedHours = evt.detail as OpeningHours[]
    const validationError = this.validateRegularHours(updatedHours)
    if (validationError) {
      this.regularHoursError = validationError
      return
    }
  
    const clearedOpeningHours = this.openingHours.filter(entry => !entry.dayOfWeek)
    const nextOpeningHours = [...clearedOpeningHours, ...updatedHours]
    this.openingHours = nextOpeningHours
    this.regularHoursError = ''
  
    console.log('Updated opening hours in parent:', this.openingHours)
    void this.enqueueRegularHoursSave(nextOpeningHours)
  }

  private async enqueueRegularHoursSave(nextOpeningHours: OpeningHours[]): Promise<void> {
    if (this.isSavingRegularHours) {
      this.pendingRegularHours = nextOpeningHours
      return
    }

    this.isSavingRegularHours = true

    try {
      await this.saveRegularHours(nextOpeningHours)
    } catch (error) {
      console.error('Error saving opening hours:', error)
      this.regularHoursError = 'Öffnungszeiten konnten nicht gespeichert werden. Die bisherigen Daten wurden neu geladen.'
      await this.getInitialOpeningHours()
    } finally {
      this.isSavingRegularHours = false
      if (this.pendingRegularHours) {
        const pendingHours = this.pendingRegularHours
        this.pendingRegularHours = null
        await this.enqueueRegularHoursSave(pendingHours)
      }
    }
  }

  async saveRegularHours(openingHours: OpeningHours[]) {
    const auth = { restaurantId: this.restaurantId, key: this.key }
    const numericId = parseInt(this.restaurantId, 10)
    const payload = this.prepareOpeningTimePayload(openingHours)

    const currentOpeningHours = await ApiService.get<OpeningTimeEntry[]>(
      API_ENDPOINTS.openingTimes(numericId),
      auth
    )

    await this.reconcileOpeningTimes(currentOpeningHours, payload, auth)

    console.log('Successfully saved opening hours')
    await this.getInitialOpeningHours()
  }

  private validateRegularHours(openingHours: OpeningHours[]): string {
    const regularDays = new Set(openingHours.filter(entry => entry.dayOfWeek).map(entry => entry.dayOfWeek))
    const hasUnknownDay = Array.from(regularDays).some(day => !DAY_ORDER_FULL.includes(day as any))

    if (hasUnknownDay) {
      return 'Öffnungszeiten enthalten einen unbekannten Wochentag.'
    }

    if (DAY_ORDER_FULL.some(day => !regularDays.has(day))) {
      return 'Öffnungszeiten wurden nicht gespeichert, weil nicht alle Wochentage geladen wurden.'
    }

    return ''
  }

  private prepareOpeningTimePayload(openingHours: OpeningHours[]): OpeningTimePayload[] {
    const payload = mapSchemaOrgToCustom(openingHours) as OpeningTimePayload[]

    return payload.map(entry => {
      if (entry.close > 86340000 || entry.open > 86340000) {
        throw new Error(`Opening time exceeds backend supported range: ${JSON.stringify(entry)}`)
      }

      return {
        ...entry,
        restaurantId: Number(this.restaurantId),
        hhmmOpen: formatTime(entry.open),
        hhmmClose: formatTime(entry.close),
      }
    })
  }

  private openingTimeSignature(entry: OpeningTimePayload | OpeningTimeEntry): string {
    const days = DAY_ORDER_SHORT.map(day => entry[day] ? '1' : '0').join('')
    return `${days}|${entry.open}|${entry.close}`
  }

  private entriesToCreate(
    currentOpeningHours: OpeningTimeEntry[],
    desiredOpeningHours: OpeningTimePayload[]
  ): OpeningTimePayload[] {
    const currentCounts = new Map<string, number>()
    currentOpeningHours.forEach(entry => {
      const signature = this.openingTimeSignature(entry)
      currentCounts.set(signature, (currentCounts.get(signature) ?? 0) + 1)
    })

    return desiredOpeningHours.filter(entry => {
      const signature = this.openingTimeSignature(entry)
      const count = currentCounts.get(signature) ?? 0
      if (count > 0) {
        currentCounts.set(signature, count - 1)
        return false
      }
      return true
    })
  }

  private entriesToDelete(
    currentOpeningHours: OpeningTimeEntry[],
    desiredOpeningHours: OpeningTimePayload[]
  ): OpeningTimeEntry[] {
    const desiredCounts = new Map<string, number>()
    desiredOpeningHours.forEach(entry => {
      const signature = this.openingTimeSignature(entry)
      desiredCounts.set(signature, (desiredCounts.get(signature) ?? 0) + 1)
    })

    return currentOpeningHours.filter(entry => {
      const signature = this.openingTimeSignature(entry)
      const count = desiredCounts.get(signature) ?? 0
      if (count > 0) {
        desiredCounts.set(signature, count - 1)
        return false
      }
      return true
    })
  }

  private async reconcileOpeningTimes(
    currentOpeningHours: OpeningTimeEntry[],
    desiredOpeningHours: OpeningTimePayload[],
    auth: { restaurantId: string, key: string }
  ): Promise<void> {
    const toCreate = this.entriesToCreate(currentOpeningHours, desiredOpeningHours)
    const toDelete = this.entriesToDelete(currentOpeningHours, desiredOpeningHours)

    for (const entry of toCreate) {
      console.log('Adding opening time:', JSON.stringify(entry))
      await ApiService.post(
        API_ENDPOINTS.addOpeningTime(),
        entry,
        auth
      )
    }

    for (const entry of toDelete) {
      console.log('Removing opening time:', JSON.stringify(entry))
      await ApiService.post(
        API_ENDPOINTS.removeOpeningTime(),
        { id: entry.id },
        auth
      )
    }
  }


  handleHolidayHours(evt: CustomEvent) {
    console.log('evt.detail from holidayhourschange')
    console.log(evt.detail)
    const clearedOpeningHours = this.openingHours.filter(entry => !entry.name)
    this.openingHours = [...clearedOpeningHours, ...evt.detail]
    console.log(this.openingHours)
    // todo: add holiday hours to opening hours and PUT on server 
  }

  @state() state: 'dashboard' | 'regularhours' | 'holidayhours' = 'dashboard'

  render() {
    console.log('openingHours for id:', this.restaurantId)
    return html`
     
      <sp-theme scale="large" color="light">
        ${this.regularHoursError ? html`<p class="error">${this.regularHoursError}</p>` : nothing}

        ${
          this.state === 'dashboard' 
          ? html`
            <sl-card title="Öffnungszeiten für restarant nr. ${ this.restaurantId }">
              <temp-toggle-open .currentHours=${ this.openingHours }
              restaurantId=${ this.restaurantId }
              .credentials=${ createBasicAuthHeader(this.restaurantId, this.key) }
              @temporaryhourschange=${ this.handleTemporaryHours }></temp-toggle-open>  
            </sl-card>

             <div class="cta-container">
               <sl-button class="cta" size="large" variant="primary"
                ?disabled=${ !this.openingHoursLoaded || this.isSavingRegularHours }
                @click=${ ()=> this.state = 'regularhours'}
                >Reguläre Öffnungszeiten</sl-button>

               <sl-button class="cta" size="large" variant="primary"
                @click=${ ()=> this.state = 'holidayhours'}
                >Öffnungszeiten an Feiertagen</sl-button>
             </div>
          `: nothing
        }
        

        ${
          this.state === 'regularhours'
          ? html`
          <a @click=${ ()=> this.state = 'dashboard'}>&laquo;</a>
          <br />
          <h2>Reguläre Öffnungszeiten</h2>
          <sl-card>
            ${this.openingHoursLoaded ? html`
              <regular-hours .currentHours=${ this.openingHours }
                @regularhourschange=${ this.handleRegularHours }></regular-hours>
            ` : html`<p>Öffnungszeiten werden geladen...</p>`}
          </sl-card> 
         `: nothing
        }        
        

        ${
          this.state === 'holidayhours'
          ? html`
           <a @click=${ ()=> this.state = 'dashboard'}>&laquo;</a>
             <br />
            <holiday-hours
            .restaurantId=${ this.restaurantId }
            .key=${ this.key }
            .currentHours=${ this.openingHours }
              @holidayhourschange=${ this.handleHolidayHours }></holiday-hours>      

         `: nothing
        }

       
      </sp-theme>
    `
  }

  static styles = [sharedStyles, styles]
}

declare global {
  interface HTMLElementTagNameMap {
    'opening-hours': OpeningHoursCmp
  }
}
